import { z } from "zod";
import { SlideDomainEnum } from "./slide";

// Pronunciation score breakdown from Azure Speech Service
export const PronunciationScoreSchema = z.object({
  accuracyScore: z.number().min(0).max(100),
  fluencyScore: z.number().min(0).max(100),
  completenessScore: z.number().min(0).max(100),
  pronScore: z.number().min(0).max(100),
});

// Syllable-level pronunciation score
export const SyllableScoreSchema = z.object({
  syllable: z.string(),
  score: z.number(),
});

// Answer types - based on different interaction methods
export const AnswerTypeEnum = z.enum([
  "multiple_choice", // Multiple choice
  "fill_in_blank", // Fill in the blank
  "text_input", // Text input
  "audio_recording", // Audio recording
  "conversation", // Conversation interaction
  "drag_and_drop", // Drag and drop
  "matching", // Matching
  "word", // Word
  "grammar", // Grammar
  "response", // Response
  "phonics", // Phonics
  "reading", // Reading
  "vocabulary", //Vocabulary
  "other", // Other
]);

// Single answer record
export const AnswerRecordSchema = z.object({
  id: z.string(),
  attemptNumber: z.number().nullish(), // Attempt number
  answer: z.object({
    texts: z.array(z.string()),
    audioIds: z.array(z.string()),
    scores: z.array(z.number()),
  }),
  isCorrect: z.boolean().nullish(),
  timestamp: z.number().nullish(), // Unix timestamp in milliseconds
  timeTaken: z.number().nullish(), // Time taken (milliseconds)
  hintsUsed: z.array(z.string()).default([]), // Hints used
  audioKey: z.string().nullish(), // S3 key — resolved to a signed/bare URL as `audioUrl` in the GraphQL response
  audioUrl: z.string().nullish(), // Signed/bare playback URL resolved from `audioKey` on read
  feedback: z.string().nullish(), // AI or system feedback
  pronunciationData: z.array(SyllableScoreSchema).nullish(), // Syllable-level pronunciation scores
});

// Single conversation message
export const ConversationMessageSchemaV2 = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant", "system"]),
  content: z.string(),
  timestamp: z.number(), // Unix timestamp in milliseconds
  audioKey: z.string().nullish(), // S3 key — resolved to a signed/bare URL as `audioUrl` in the GraphQL response
  audioUrl: z.string().nullish(), // Signed/bare playback URL resolved from `audioKey` on read
  pronunciationScore: PronunciationScoreSchema.nullish(), // Pronunciation assessment score
  metadata: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).nullish(), // Additional metadata
});

// Conversation record session (contains complete AI conversation exchanges)
export const ConversationRecordSchema = z.object({
  id: z.string(),
  sessionType: z.enum(["practice", "feedback", "hint", "explanation"]).nullish(), // Conversation type
  messages: z.array(ConversationMessageSchemaV2), // Complete list of conversation messages
  startedAt: z.date(),
  endedAt: z.date().nullish(),
  totalMessages: z.number().default(0),
  summary: z.string().nullish(), // AI-generated conversation summary
  relatedAnswerId: z.string().nullish(), // Related answer record ID
});

// Main SlideView schema - stores student learning records
export const SlideViewSchema = z.object({
  id: z.string(),
  slideId: z.string(), // Related slide
  userId: z.string(), // Student ID
  lessonId: z.string(), // Lesson ID

  // Answer status
  status: z.enum(["not_started", "in_progress", "completed", "skipped"]),
  answerType: AnswerTypeEnum,

  // Frozen from the slide when an answer lands, so re-categorising a slide never moves earned scores.
  domains: z.array(SlideDomainEnum).default([]),

  // Answer records (may have multiple attempts)
  answerRecords: z.array(AnswerRecordSchema).default([]),

  // Conversation records (if AI conversation interaction exists)
  conversationRecords: z.array(ConversationRecordSchema).default([]).optional(),

  // Statistics
  totalAttempts: z.number().default(0),
  correctAttempts: z.number().default(0),
  totalTimeSpent: z.number().default(0), // Total time spent (milliseconds)
  score: z.number().min(0).max(100).optional(), // Score (0-100)

  // Learning progress metadata
  startedAt: z.date().optional(),
  completedAt: z.date().optional(),
  lastAttemptAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),

  // Additional analytics data
  knowledgeAreaPerformance: z
    .record(
      z.string(),
      z.object({
        attempts: z.number(),
        correct: z.number(),
        avgTimeSpent: z.number(),
      })
    )
    .optional(), // Statistics by knowledge area

  // Metadata
  metadata: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).nullish(), // Additional metadata
});

// Batch learning records (for statistics across lessons or multiple slides)
export const BatchSlideViewSchema = z.object({
  userId: z.string(),
  lessonId: z.string(),
  slideViews: z.array(SlideViewSchema),
  overallScore: z.number().min(0).max(100).optional(),
  completionRate: z.number().min(0).max(100), // Completion rate
  totalTimeSpent: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// TypeScript types
export type PronunciationScore = z.infer<typeof PronunciationScoreSchema>;
export type SyllableScore = z.infer<typeof SyllableScoreSchema>;
export type SlideView = z.infer<typeof SlideViewSchema>;
export type AnswerRecord = z.infer<typeof AnswerRecordSchema>;
export type ConversationMessageV2 = z.infer<typeof ConversationMessageSchemaV2>;
export type ConversationRecord = z.infer<typeof ConversationRecordSchema>;
export type BatchSlideView = z.infer<typeof BatchSlideViewSchema>;
export type AnswerType = z.infer<typeof AnswerTypeEnum>;
